import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { BrandWordmark } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Lock, Layers, Zap } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState([]);

  useEffect(() => { if (user) nav(user.role === "Dealer" ? "/portal" : "/"); }, [user, nav]);
  useEffect(() => { api.get("/auth/demo-accounts").then((r) => setDemo(r.data.accounts)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      nav(u.role === "Dealer" ? "/portal" : "/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const quick = (acc) => { setEmail(acc.email); setPassword(demoPw(acc.role)); };
  const demoPw = (role) => ({
    "Super Administrator": "SuperAdmin@123", "Corporate Scheme Admin": "Admin@123",
    "Finance Approver": "Finance@123", "RSO Scheme Creator": "RSO@123",
    "Management Viewer": "Viewer@123", "Dealer": "Dealer@123",
  }[role] || "");

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 adani-navy-gradient p-12 relative overflow-hidden">
        <div className="relative z-10"><BrandWordmark light /></div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl font-extrabold text-white leading-tight">
            Enterprise DIY Scheme Digitization &amp; Incentive Analytics
          </h1>
          <p className="text-cyan-100/80 mt-4 text-base leading-relaxed">
            Configure schemes, run auditable calculation engines, and settle dealer incentives —
            with an immutable ledger and full drill-down from Company to Invoice line.
          </p>
          <div className="mt-8 space-y-3">
            {[["Three calculation engines", Zap], ["Immutable audit ledger", ShieldCheck], ["100% metadata-driven", Layers]].map(([t, I]) => (
              <div key={t} className="flex items-center gap-3 text-white/90 text-sm">
                <span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center"><I size={16} /></span>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-cyan-200/50 text-xs">Adani Cement · ACC + ACL · Confidential</div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#00A3E0]/20 blur-3xl" />
        <div className="absolute top-10 -left-10 w-64 h-64 rounded-full bg-[#D97706]/10 blur-3xl" />
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md fade-up">
          <div className="lg:hidden mb-8"><BrandWordmark /></div>
          <div className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="text-slate-500 text-sm mt-1">Access your role-based console</p>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com"
                className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input id="password" data-testid="login-password" type="password" value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  className="pl-9" required />
              </div>
            </div>
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full bg-[#0F2C59] hover:bg-[#00529B] text-white font-semibold h-11">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          {demo.length > 0 && (
            <div className="mt-8">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-2">Demo accounts</div>
              <div className="grid grid-cols-1 gap-2">
                {demo.map((a) => (
                  <button key={a.email} data-testid={`demo-account-${a.role.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => quick(a)}
                    className="text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-[#00529B] hover:bg-blue-50/50 text-sm flex justify-between items-center">
                    <span className="font-medium text-slate-700">{a.role}</span>
                    <span className="text-xs text-slate-400">{a.email}</span>
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-2">Click to prefill, then Sign in.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
